import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { errorMessage, get, put } from '../../api/client';
import type { PmLanding, PmSectionId, PmSections, PmSectionsState } from '../../api/types';
import { IconAlert, IconExternal } from '../../components/icons';
import { PageHead, Spinner, Switch, useFeedback } from '../../components/ui';
import { tk, useT } from '../../i18n';

const KEY = ['pm-sections'] as const;
const SAVE = ['pm-sections', 'save'] as const;

/** Nombre y contenido de cada sección, para reconocerla. */
const INFO: Record<PmSectionId, { name: string; hint: string }> = {
  lineup: { name: tk('Gama'), hint: tk('Las fotos de las motos de la gama, por segmento') },
  details: { name: tk('Línea de producto'), hint: tk('El carrusel con la ficha de cada moto') },
  opportunity: { name: tk('La oportunidad'), hint: tk('Las condiciones para el distribuidor, con la moto que enciende las luces') },
  about: { name: tk('Sobre la marca'), hint: tk('La foto de la rueda que gira y el texto de la marca') },
  history: { name: tk('Historia'), hint: tk('La línea de tiempo (sus hitos se editan en Historia)') },
  markets: { name: tk('Mercados'), hint: tk('El mapa con los concesionarios y los territorios libres') },
  faq: { name: tk('Preguntas frecuentes'), hint: tk('Las preguntas y sus respuestas') },
  values: { name: tk('Valores'), hint: tk('Los videos Elegance · Timeless · Legacy') },
  news: { name: tk('Noticias'), hint: tk('El carrusel de noticias (la página de cada noticia sigue existiendo)') },
};

/** Las que no se ocultan, en su lugar de la página: sin ellas la landing pierde su función. */
const FIXED = {
  hero: { name: tk('Portada'), hint: tk('El video de la portada con su botón') },
  form: { name: tk('Formulario'), hint: tk('La solicitud en partners; «Hablemos» con el calendario en booking') },
};

const LANDING: Record<PmLanding, string> = { partners: 'Partners', booking: 'Booking' };

type Row = { fixed: keyof typeof FIXED } | { id: PmSectionId };

/** Las secciones de las landings de Phelon & Moore: cuáles se ven en cada una. */
export function PmSectionsPage() {
  const { t } = useT();
  const qc = useQueryClient();
  const { toast } = useFeedback();
  const { data, isPending, error } = useQuery({ queryKey: KEY, queryFn: () => get<PmSectionsState>('/admin/pm/sections') });

  // Los cambios van en fila (uno a la vez) y solo el último pinta la respuesta: dos clics seguidos
  // no hacen saltar los interruptores a un estado intermedio.
  const save = useMutation({
    mutationKey: SAVE,
    scope: { id: 'pm-sections' },
    mutationFn: (sections: PmSections) => put<PmSectionsState>('/admin/pm/sections', sections),
    onSuccess: (res) => {
      if (qc.isMutating({ mutationKey: SAVE }) <= 1) qc.setQueryData(KEY, res);
      void qc.invalidateQueries({ queryKey: ['pm-publish'] });
    },
    onError: (e) => {
      toast(errorMessage(e), 'error');
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });

  const toggle = (landing: PmLanding, id: PmSectionId, visible: boolean) => {
    const cur = qc.getQueryData<PmSectionsState>(KEY);
    if (!cur) return;
    const sections = { ...cur.sections, [landing]: { ...cur.sections[landing], [id]: visible } };
    qc.setQueryData<PmSectionsState>(KEY, { ...cur, sections });
    const vars = { section: t(INFO[id]?.name ?? id), landing: LANDING[landing] };
    save.mutate(sections, {
      onSuccess: () => toast(visible ? t('«{section}» vuelve a {landing} al publicar', vars) : t('«{section}» sale de {landing} al publicar', vars)),
    });
  };

  // En el orden de la página: la portada arriba y el formulario antes de los valores.
  const rows: Row[] = [];
  if (data) {
    rows.push({ fixed: 'hero' });
    for (const id of data.order) {
      if (id === 'values') rows.push({ fixed: 'form' });
      rows.push({ id });
    }
    if (!data.order.includes('values')) rows.push({ fixed: 'form' });
  }

  return (
    <>
      <PageHead
        kicker={t('Landing')}
        title={t('Secciones')}
        description={t(
          'Qué secciones muestra cada landing. Las que apagues salen de la página y de su enlace en el menú, en todos los idiomas y en las versiones Europa y Global. Los cambios se ven después de publicar.',
        )}
      >
        {data?.landings.map(
          (l) =>
            data.urls[l] && (
              <a key={l} className="btn" href={data.urls[l]!} target="_blank" rel="noreferrer">
                <IconExternal /> {t('Ver {landing}', { landing: LANDING[l] })}
              </a>
            ),
        )}
      </PageHead>

      {isPending ? (
        <Spinner />
      ) : error || !data ? (
        <div className="notice error">{errorMessage(error)}</div>
      ) : (
        <div className="stack">
          {!data.ready && (
            <div className="notice warn">
              <IconAlert width={16} style={{ flex: 'none', marginTop: 2 }} />
              <span>{t('Falta aplicar la migración de la base para poder guardar (npm run db:deploy). Mientras tanto las landings se publican con todas las secciones.')}</span>
            </div>
          )}
          <div className="panel" style={{ overflowX: 'auto' }}>
            <table className="table table-stack sections-table">
              <thead>
                <tr>
                  <th>{t('Sección')}</th>
                  {data.landings.map((l) => (
                    <th key={l}>{LANDING[l]}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) =>
                  'fixed' in r ? (
                    <tr key={r.fixed} className="is-fixed">
                      <td>
                        <strong>{t(FIXED[r.fixed].name)}</strong>
                        <div className="faint section-hint">{t(FIXED[r.fixed].hint)}</div>
                      </td>
                      {data.landings.map((l) => (
                        <td key={l} data-label={LANDING[l]}>
                          <span className="faint">{t('Siempre visible')}</span>
                        </td>
                      ))}
                    </tr>
                  ) : (
                    <tr key={r.id} className={data.landings.every((l) => !data.sections[l][r.id]) ? 'is-off' : ''}>
                        <td>
                          <strong>{t(INFO[r.id]?.name ?? r.id)}</strong>
                          {INFO[r.id] && <div className="faint section-hint">{t(INFO[r.id].hint)}</div>}
                        </td>
                        {data.landings.map((l) => {
                          const on = data.sections[l][r.id];
                          return (
                            <td key={l} data-label={LANDING[l]}>
                              <Switch checked={on} disabled={!data.ready} onChange={(v) => toggle(l, r.id, v)} label={on ? t('Visible') : t('Oculta')} />
                            </td>
                          );
                        })}
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          </div>
          <p className="faint" style={{ fontSize: 13, margin: 0 }}>
            {t('Si ocultas las noticias, la página de cada noticia sigue en el sitio (por los enlaces que ya se compartieron); solo deja de verse el carrusel.')}
          </p>
        </div>
      )}
    </>
  );
}
